import { IssueType } from '../../database/issue.interface';
import type { IssueNoteEntity } from '../../entity/issue-note.entity';
import type { IssueEntity } from '../../entity/issue.entity';
import type { IssueAttachmentModel } from '../issue-attachment.dto';

/**
 * Portal Issue DTOs (PD-13705, PD-13773)
 *
 * Contract for the storefront "Submit an Issue" widget — a signed-in
 * customer sends a categorized inquiry that lands as an `issue` record
 * in the CRM, linked to their active client — and for the follow-up
 * visibility surface: the customer's own submitted issues (list +
 * detail + communication thread with the support team).
 */

/**
 * Categories a portal customer may pick from. Deliberately a subset of
 * `IssueType` — `Accounting` is an internal triage category and is not
 * offered to customers.
 */
export const PORTAL_ISSUE_TYPES: ReadonlyArray<IssueType> = [
	IssueType.QUESTION,
	IssueType.REQUEST,
	IssueType.PROBLEM,
];

/**
 * Server-enforced field limits, shared so the widget can mirror them. The
 * message limit counts the visible text of a rich message, not its markup
 * (PD-16475).
 */
export const PORTAL_ISSUE_SUBJECT_MAX_LENGTH = 255;
export const PORTAL_ISSUE_MESSAGE_MAX_LENGTH = 10000;

export interface PortalSubmitIssueRequestDto {
	/** Becomes the issue name. */
	subject: string;
	/** One of PORTAL_ISSUE_TYPES; maps to issue.type. */
	type: IssueType;
	/** Plain text; becomes the issue description. Ignored when `messageHtml` is sent. */
	message: string;
	/** PD-16475 — rich text; the plain description is derived from it. */
	messageHtml?: string | null;
	/** PD-16475 — images uploaded through POST /api/portal/issues/attachments. */
	attachmentFileIds?: Array<number>;
}

/** PD-16475 — the result of uploading one image for a portal message. */
export interface PortalIssueAttachmentUploadResponseDto {
	fileId: number;
	name: string;
	contentType: string;
}

export interface PortalSubmitIssueResponseDto {
	/** The created issue id — shown to the customer as their reference number. */
	issueId: number;
}

// --- Visibility surface (PD-13773) ---

/**
 * One row in the customer's "Support requests" list. Strictly scoped to
 * issues the signed-in portal user submitted themselves
 * (`issue.fk_public_user__id_submitter` = current public user).
 */
export type PortalIssueListItemDto = Pick<
	IssueEntity,
	'id' | 'name' | 'type' | 'status' | 'createdDate' | 'statusUpdatedDatetime'
> & {
	/**
	 * Portal-visible messages on the thread (customer replies + staff notes
	 * shared with the customer). Excludes the original description.
	 */
	messageCount: number;
};

export interface PortalIssueListResponseDto {
	issues: Array<PortalIssueListItemDto>;
}

/**
 * One entry of the customer↔support communication thread. The original
 * submission message is emitted as the first `customer` entry; later
 * entries come from `issue_note` rows that are portal-visible.
 */
export type PortalIssueMessageDto = Pick<IssueNoteEntity, 'createdDate'> & {
	id: number;
	body: string;
	/** Who wrote it — drives the chat-style alignment in the portal UI. */
	author: 'customer' | 'support';
	/** Display name (customer's own name, or the staff member's). */
	authorName: string | null;
	/** PD-16475 — the message's rich text; null for a plain-text message. */
	bodyHtml: string | null;
	/** PD-16475 — images sent with the message, with signed URLs. */
	attachments: Array<IssueAttachmentModel>;
};

export type PortalIssueDetailDto = Pick<
	IssueEntity,
	'id' | 'name' | 'type' | 'status' | 'createdDate' | 'statusUpdatedDatetime'
> & {
	thread: Array<PortalIssueMessageDto>;
};

export interface PortalIssueReplyRequestDto {
	/** The customer's reply; becomes a portal-visible issue note. */
	message: string;
	/** PD-16475 — rich text; the plain note is derived from it. */
	messageHtml?: string | null;
	/** PD-16475 — images uploaded through POST /api/portal/issues/attachments. */
	attachmentFileIds?: Array<number>;
}

export interface PortalIssueReplyResponseDto {
	/** The refreshed thread after the reply was appended. */
	detail: PortalIssueDetailDto;
}

/**
 * PD-16525 — the customer's answer to "did that solve it?" on a request the
 * team marked Resolved. `solved: true` closes it (Done); `false` hands it
 * back to the team (Needs Reply).
 */
export interface PortalIssueConfirmResolutionRequestDto {
	solved: boolean;
}

export interface PortalIssueConfirmResolutionResponseDto {
	/** The refreshed request after the answer was recorded. */
	detail: PortalIssueDetailDto;
}
